// Single analytics entry point: GA4 (gtag.js) + BalkanPuls first-party collector.
// Basic Consent Mode: nothing is loaded, stored or sent before analytics consent.
const GA_ID = process.env.REACT_APP_GA4_ID;
const API = process.env.REACT_APP_BACKEND_URL;
const KEY = "bp-consent";
const VKEY = "bp-vid";
const SKEY = "bp-sid";
const VERSION = 1;
const QKEY = "bp-qa";
// Documented QA exclusion: ?bp_qa=1 marks this browser as internal test traffic (?bp_qa=0 removes it).
try {
  const qa = new URLSearchParams(window.location.search).get("bp_qa");
  if (qa === "1") localStorage.setItem(QKEY, "1");
  if (qa === "0") localStorage.removeItem(QKEY);
} catch { /* storage unavailable */ }
const DENIED = { analytics_storage: "denied", ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied" };
const GA_EVENTS = { article_view: "article_view", engaged_read: "article_engaged_read", share_click: "article_share_click",
  article_save: "article_save", search_performed: "search_performed", partner_website_click: "partner_link_click",
  newsletter_signup: "newsletter_signup", ai_question_submitted: "ai_question_submitted", ai_answer_completed: "ai_answer_completed",
  ai_answer_failed: "ai_answer_failed", ai_feedback_submitted: "ai_feedback_submitted" };
let loaded = false;
let lastPath = null;
let queue = [];
let timer = null;

window.dataLayer = window.dataLayer || [];
function gtag() {
  window.dataLayer.push(arguments); // eslint-disable-line prefer-rest-params
}
if (!window.__bpConsentDefault) {
  window.__bpConsentDefault = true;
  gtag("consent", "default", { ...DENIED, wait_for_update: 500 });
}

export const getConsent = () => {
  try {
    const c = JSON.parse(localStorage.getItem(KEY) || "null");
    return c && c.v === VERSION ? c : null;
  } catch {
    return null;
  }
};

const isPrivate = (path) => path.startsWith("/admin") || path.startsWith("/partner/");
const granted = () => !!getConsent()?.analytics;
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`);

// ---------- first-party identifiers (only with consent) ----------
let returning = false;
const ids = () => {
  let v = localStorage.getItem(VKEY);
  if (!v) {
    v = uid();
    localStorage.setItem(VKEY, v);
  } else if (!sessionStorage.getItem(SKEY)) returning = true;
  let s = JSON.parse(sessionStorage.getItem(SKEY) || "null");
  if (!s || Date.now() - s.last > 30 * 60 * 1000) {
    const p = new URLSearchParams(window.location.search);
    const ref = document.referrer ? new URL(document.referrer).hostname : "";
    s = { id: uid(), returning: returning || !!s, source: { utm_source: p.get("utm_source") || "", utm_medium: p.get("utm_medium") || "",
      utm_campaign: p.get("utm_campaign") || "", utm_content: p.get("utm_content") || "",
      referrer_host: ref === window.location.hostname ? "" : ref, landing: window.location.pathname } };
  }
  s.last = Date.now();
  sessionStorage.setItem(SKEY, JSON.stringify(s));
  return { v, s };
};

const flush = (beacon = false) => {
  clearTimeout(timer);
  timer = null;
  if (!queue.length || !granted()) { queue = []; return; }
  const { v, s } = ids();
  const body = JSON.stringify({ session_id: s.id, visitor_id: v, returning: s.returning, consent: "analytics_granted", source: s.source, events: queue.splice(0, 25), test: localStorage.getItem(QKEY) === "1" });
  const url = `${API}/api/collect`;
  if (beacon && navigator.sendBeacon) navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
  else fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
  if (queue.length) flush(beacon);
};
window.addEventListener("pagehide", () => flush(true));
document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flush(true); });

const clearGaCookies = () => {
  document.cookie.split(";").map((c) => c.split("=")[0].trim()).filter((n) => n === "_ga" || n.startsWith("_ga_")).forEach((n) => {
    const host = window.location.hostname;
    [host, `.${host}`, `.${host.split(".").slice(-2).join(".")}`, ""].forEach((d) => {
      document.cookie = `${n}=; Max-Age=0; path=/;${d ? ` domain=${d};` : ""}`;
    });
  });
};

const load = () => {
  if (loaded || !GA_ID) return;
  loaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  s.onload = () => fetch(`${API}/api/public/analytics/tag-loaded`, { method: "POST" }).catch(() => {});
  document.head.appendChild(s);
  gtag("js", new Date());
  // Page views are sent manually on route changes; the automatic one is disabled to avoid duplicates.
  gtag("config", GA_ID, { send_page_view: false, allow_google_signals: false, allow_ad_personalization_signals: false });
};

const applyConsent = (c) => {
  gtag("consent", "update", { analytics_storage: c.analytics ? "granted" : "denied", ad_storage: c.ads ? "granted" : "denied",
    ad_user_data: c.ads ? "granted" : "denied", ad_personalization: c.ads ? "granted" : "denied" });
  window[`ga-disable-${GA_ID}`] = !c.analytics;
  if (c.analytics && !isPrivate(window.location.pathname)) load();
  if (!c.analytics) {
    clearGaCookies();
    queue = [];
    localStorage.removeItem(VKEY);
    sessionStorage.removeItem(SKEY);
  }
};

export const saveConsent = (choice) => {
  const wasLoaded = loaded && granted();
  const c = { v: VERSION, analytics: !!choice.analytics, ads: !!choice.ads, ts: new Date().toISOString() };
  localStorage.setItem(KEY, JSON.stringify(c));
  applyConsent(c);
  window.dispatchEvent(new Event("bp-consent-change"));
  // A reload removes gtag.js completely, so no hit queued before withdrawal can be sent afterwards.
  if (wasLoaded && !c.analytics) { setTimeout(() => window.location.reload(), 300); return; }
  if (c.analytics) trackPageView(window.location.pathname + window.location.search, true);
};

// Called on every route change: GA is disabled on private (admin) routes and without consent.
export const syncRoute = (path) => {
  if (isPrivate(path)) lastPath = null;
  if (GA_ID) window[`ga-disable-${GA_ID}`] = isPrivate(path) || !granted();
};

export const initAnalytics = () => {
  const c = getConsent();
  if (c) applyConsent(c);
};

const send = (name, params = {}, contentId = null) => {
  const path = window.location.pathname;
  if (!granted() || isPrivate(path)) return;
  queue.push({ event_id: uid(), name, path, content_id: contentId, props: params });
  if (queue.length >= 10) flush();
  else if (!timer) timer = setTimeout(flush, 2000);
  const ga = name === "page_view" ? "page_view" : GA_EVENTS[name];
  if (ga && loaded && !window[`ga-disable-${GA_ID}`]) {
    const extra = name === "page_view" ? { page_location: window.location.origin + path + window.location.search, page_title: document.title } : {};
    gtag("event", ga, { ...params, ...(contentId ? { content_id: contentId } : {}), ...extra });
  }
};

export const trackPageView = (path, force = false) => {
  if (isPrivate(path) || !granted()) return;
  load();
  if (!force && path === lastPath) return;
  lastPath = path;
  send("page_view");
};

// Only documented, non-personal parameters (validated again on the server).
export const track = (name, params = {}, contentId = null) => send(name, params, contentId);

export const GA_MEASUREMENT_ID = GA_ID;
